@if (flash()) { <div class="iss-flash"></div> }

@if (open() && ctx()) {
  <div class="iss-overlay" (mousedown)="backdrop($event)">
    <div class="iss-modal" role="dialog" [attr.aria-label]="'issues.issueReporter.reportProblem' | translate">

      <!-- Header -->
      <div class="iss-modal-head">
        <div class="iss-modal-mark"><app-icon name="Bug" [size]="16"></app-icon></div>
        <div class="flex1">
          <div class="iss-modal-title">{{ 'issues.issueReporter.reportProblem' | translate }}</div>
          <div class="text-tiny muted">
            {{ 'issues.issueReporter.capturedFrom' | translate }} <b style="color:var(--text-muted)">{{ ctx()!.moduleShort }}</b> · {{ 'issues.issueReporter.pressEscToCancel' | translate }}
          </div>
        </div>
        <button type="button" class="icon-btn" (click)="open.set(false)">
          <app-icon name="Close" [size]="16"></app-icon>
        </button>
      </div>

      <div class="iss-modal-body">
        <!-- Left: snapshot + annotate toolbar -->
        <div class="iss-cap-col">
          <div class="iss-cap-toolbar">
            <span class="text-tiny uppercase bold muted" style="letter-spacing:0.06em">{{ 'issues.issueReporter.snapshot' | translate }}</span>
            <div class="iss-tool-group">
              <button type="button" class="iss-tool" [class.on]="tool() === 'box'"
                      (click)="tool.set('box')" [title]="'issues.issueReporter.drawABox' | translate">
                <app-icon name="BoxSelect" [size]="14"></app-icon> {{ 'issues.issueReporter.box' | translate }}
              </button>
              <button type="button" class="iss-tool" [class.on]="tool() === 'arrow'"
                      (click)="tool.set('arrow')" [title]="'issues.issueReporter.drawAnArrow' | translate">
                <app-icon name="ArrowTool" [size]="14"></app-icon> {{ 'issues.issueReporter.arrow' | translate }}
              </button>
              <button type="button" class="iss-tool"
                      [disabled]="annos().length === 0"
                      (click)="undoAnno()" [title]="'issues.issueReporter.undo' | translate">
                <app-icon name="Undo" [size]="14"></app-icon>
              </button>
            </div>

            <!-- Capture area. Re-shoots on change, so the preview always matches the choice.
                 The reporter's own overlay is filtered out of the capture, so switching here
                 does not photograph this modal. -->
            <div class="iss-tool-group" role="group"
                 [attr.aria-label]="'issues.issueReporter.captureArea' | translate">
              <button type="button" class="iss-tool" [class.on]="!viewportOnly()"
                      [disabled]="capturing()"
                      (click)="setViewportOnly(false)"
                      [title]="'issues.issueReporter.fullPageHint' | translate">
                <app-icon name="Image" [size]="14"></app-icon>
                {{ 'issues.issueReporter.fullPage' | translate }}
              </button>
              <button type="button" class="iss-tool" [class.on]="viewportOnly()"
                      [disabled]="capturing()"
                      (click)="setViewportOnly(true)"
                      [title]="'issues.issueReporter.visibleAreaHint' | translate">
                <app-icon name="Eye" [size]="14"></app-icon>
                {{ 'issues.issueReporter.visibleArea' | translate }}
              </button>
            </div>
          </div>
          <app-iss-snapshot
            [shot]="shot()"
            [annotations]="annos()"
            [editable]="true"
            [tool]="tool()"
            [boxH]="360"
            [placeholderModule]="ctx()!.moduleShort"
            (annotationAdded)="addAnno($event)"
          ></app-iss-snapshot>
          <div class="text-tiny muted mt-6">
            <app-icon name="Crosshair" [size]="11"></app-icon>
            {{ 'issues.issueReporter.dragToPoint' | translate }}
          </div>
        </div>

        <!-- Right: form + auto-captured context -->
        <div class="iss-form-col">
          <div class="field">
            <label class="field-label">{{ 'issues.issueReporter.title' | translate }}</label>
            <input class="input" [placeholder]="'issues.issueReporter.titlePh' | translate"
                   maxlength="90" [value]="title()"
                   (input)="title.set($any($event.target).value)" autofocus />
          </div>
          <div class="field">
            <label class="field-label">{{ 'issues.issueReporter.whatHappened' | translate }}</label>
            <textarea class="input" rows="4" style="resize:vertical"
                      [placeholder]="'issues.issueReporter.descPh' | translate"
                      [value]="desc()"
                      (input)="desc.set($any($event.target).value)"></textarea>
          </div>
          <div class="field">
            <label class="field-label">{{ 'issues.issueReporter.severity' | translate }}</label>
            <div class="iss-sev-seg">
              @for (s of severities; track s.id) {
                <button type="button" [class]="sev() === s.id ? 'on ' + s.kind : ''"
                        (click)="sev.set(s.id)">{{ s.label }}</button>
              }
            </div>
            <div class="text-tiny muted mt-6">
              <app-icon name="Info" [size]="11" color="var(--info)"></app-icon> {{ sevHint() }}
            </div>
          </div>

          <div class="iss-ctx">
            <div class="text-tiny uppercase bold muted" style="letter-spacing:0.06em;margin-bottom:8px">
              {{ 'issues.issueReporter.autoCaptured' | translate }}
            </div>
            <div class="iss-ctx-grid">
              <span class="iss-ctx-k"><app-icon name="Pin" [size]="11"></app-icon> {{ 'issues.issueReporter.location' | translate }}</span>
              <span class="iss-ctx-v">
                {{ ctx()!.moduleShort }}
                <span class="mono text-tiny muted">{{ ctx()!.hash }}</span>
              </span>
              <span class="iss-ctx-k"><app-icon name="Users" [size]="11"></app-icon> {{ 'issues.issueReporter.reporter' | translate }}</span>
              <span class="iss-ctx-v">
                {{ me().name }} <span class="text-tiny muted">· {{ me().role }}</span>
              </span>
              <span class="iss-ctx-k"><app-icon name="Clock" [size]="11"></app-icon> {{ 'issues.issueReporter.when' | translate }}</span>
              <span class="iss-ctx-v">{{ ctxFmt()?.greg }}, {{ ctxFmt()?.time }}</span>
              <span class="iss-ctx-k"><app-icon name="Dashboard" [size]="11"></app-icon> {{ 'issues.issueReporter.environment' | translate }}</span>
              <span class="iss-ctx-v">
                {{ ctx()!.env.viewport }} · {{ ctx()!.env.theme }} · {{ ctx()!.env.browser }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="iss-modal-foot">
        <div class="text-tiny muted">
          <app-icon name="Bug" [size]="12"></app-icon> {{ 'issues.issueReporter.filesToTechTeam' | translate }}
        </div>
        <div class="row gap-8">
          <button type="button" class="btn" (click)="open.set(false)">{{ 'issues.issueReporter.cancel' | translate }}</button>
          <button type="button" class="btn primary" [disabled]="!valid()" (click)="submit()">
            <app-icon name="Send" [size]="13"></app-icon> {{ 'issues.issueReporter.submitIssue' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
}
